import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
	ArrowLeft,
	Settings2,
	ShieldAlert,
	TextCursorInput,
	PanelLeft,
	SlidersHorizontal,
	FileText,
	Briefcase,
} from "lucide-react";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { cn } from "@/v2/lib/utils";

const sections = [
	{ label: "General", path: "/settings/preferences", icon: Settings2 },
	{ label: "Tracking", path: "/settings/tracking", icon: SlidersHorizontal },
	{ label: "Narratives", path: "/settings/narratives", icon: FileText },
	{
		label: "Abbreviations",
		path: "/settings/abbreviations",
		icon: TextCursorInput,
	},
	{ label: "My Matters", path: "/settings/my-matters", icon: Briefcase },
	{
		label: "Restricted Words",
		path: "/settings/restricted-words",
		icon: ShieldAlert,
	},
];

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
	const navigate = useNavigate();

	return (
		<div className="flex flex-col gap-1 p-3">
			{/* Back to app */}
			<button
				onClick={() => {
					navigate("/entries");
					onNavigate?.();
				}}
				className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors mb-2"
			>
				<ArrowLeft className="size-4" />
				Back to app
			</button>

			{/* Nav links */}
			<nav className="flex flex-col gap-0.5">
				{sections.map((s) => (
					<NavLink
						key={s.path}
						to={s.path}
						onClick={onNavigate}
						className={({ isActive }) =>
							cn(
								"flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
								isActive
									? "bg-accent text-accent-foreground"
									: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
							)
						}
					>
						<s.icon className="size-4" />
						<span className="flex-1">{s.label}</span>
					</NavLink>
				))}
			</nav>
		</div>
	);
}

export default function SettingsLayout() {
	const isMobile = useIsMobile();
	const location = useLocation();
	const [drawerOpen, setDrawerOpen] = useState(false);

	// Close drawer on navigation
	useEffect(() => setDrawerOpen(false), [location.pathname]);

	if (isMobile) {
		return (
			<div className="flex h-dvh flex-col bg-background">
				{/* Mobile header */}
				<header className="flex h-14 items-center gap-2 border-b border-border px-4">
					<button
						onClick={() => setDrawerOpen(true)}
						className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
					>
						<PanelLeft className="size-5" />
					</button>
					<NavLink
						to="/entries"
						className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
					>
						<ArrowLeft className="size-4" />
						Settings
					</NavLink>
				</header>

				{/* Content */}
				<main className="flex-1 overflow-y-auto">
					<div className="p-4">
						<Outlet />
					</div>
				</main>

				{/* Drawer */}
				<DialogPrimitive.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
					<DialogPrimitive.Portal>
						<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in data-[state=closed]:fade-out duration-200" />
						<DialogPrimitive.Content
							className={cn(
								"fixed inset-y-0 left-0 z-50 w-72 bg-background border-r border-border shadow-lg",
								"data-[state=open]:animate-in data-[state=closed]:animate-out",
								"data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
								"duration-200",
							)}
						>
							<DialogPrimitive.Title className="sr-only">
								Settings navigation
							</DialogPrimitive.Title>
							<DialogPrimitive.Description className="sr-only">
								Navigate between settings sections
							</DialogPrimitive.Description>
							<SidebarContent onNavigate={() => setDrawerOpen(false)} />
						</DialogPrimitive.Content>
					</DialogPrimitive.Portal>
				</DialogPrimitive.Root>
			</div>
		);
	}

	// Desktop — no header, sidebar + content
	return (
		<div className="flex h-dvh bg-background">
			<aside className="w-56 shrink-0 overflow-y-auto border-r border-border">
				<SidebarContent />
			</aside>
			<main className="flex-1 overflow-y-auto">
				<div className="p-6">
					<Outlet />
				</div>
			</main>
		</div>
	);
}
